import { type Drone, applySettings, drone, getRig, isAudible, primeSound } from "./rig";
import { type SoundName, VOICES, VOICE_GAP, type VoiceOptions } from "./voices";

export type { SoundName, VoiceOptions };
export { primeSound };

/**
 * When each voice is next due to sound, on the audio clock.
 *
 * On the *audio* clock rather than the wall clock, because a game is allowed to
 * book a whole run of a voice up front - a roulette ball rattling down through
 * the frets is fourteen ticks scheduled in one go - and a throttle measured
 * from the moment `play` was called would throw away thirteen of them for
 * arriving in the same millisecond.
 */
const lastPlayed = new Map<SoundName, number>();

export interface PlayOptions extends VoiceOptions {
  /**
   * Seconds to hold it back.
   *
   * For the sounds that belong to an animation rather than to the click that
   * started it - a card that lands a beat after it is dealt, a verdict that
   * waits for the board to finish. Scheduled on the audio clock rather than a
   * `setTimeout`, so it stays in time with the rest of the hit.
   */
  after?: number;
}

/**
 * Play one sound.
 *
 * Deliberately fire-and-forget and deliberately unfailing: a game calls this
 * from a click handler, from a reveal timer, and from inside a physics step,
 * and none of those places should have to think about whether the page has
 * audio, whether the player muted it, or whether the browser handed us a
 * context. Muted, unsupported, or called twice in the same millisecond, this
 * does nothing and says nothing.
 */
export const play = (name: SoundName, options: PlayOptions = {}) => {
  if (!isAudible()) return;

  const voice = VOICES[name];
  if (!voice) return;

  const rig = getRig();
  if (!rig) return;

  const at = rig.ctx.currentTime + Math.max(options.after ?? 0, 0);
  const previous = lastPlayed.get(name);

  // Compared as a distance rather than a difference: a hit landing just before
  // one that is already booked would collide with it exactly as badly as one
  // landing just after.
  if (previous !== undefined && Math.abs(at - previous) < VOICE_GAP[name] / 1000) return;

  lastPlayed.set(name, Math.max(previous ?? at, at));

  voice(rig, at, options);
};

/**
 * Where a win stops being a win and starts being a result.
 *
 * Ten times the stake. The figure is arguable and the principle is not: a
 * fanfare that fires on every winning round is a fanfare that means nothing by
 * the fifth one, and the games here are played in runs of dozens.
 */
const BIG_WIN_MULTIPLIER = 10;

/**
 * The verdict, from what the round paid.
 *
 * Every game ends the same three ways and this is the one place that decides
 * what they sound like, so a 50x dice roll and a 50x plinko drop are celebrated
 * alike. A round that paid the stake back is a push rather than a win -
 * blackjack has those outright, and so does any game whose multiplier lands on
 * exactly one.
 */
export const playOutcome = (
  outcome: { profit: number; multiplier?: number },
  options: PlayOptions = {},
) => {
  const { profit, multiplier } = outcome;

  if (profit > 0) {
    play((multiplier ?? 0) >= BIG_WIN_MULTIPLIER ? "bigWin" : "win", options);
    return;
  }
  play(profit === 0 ? "push" : "lose", options);
};

/** A round that is going up, as a note that goes up with it. */
export interface Climb {
  /**
   * Where it is now - a crash multiplier, a limbo count-up.
   *
   * `seconds` is how long to take getting there, for the climbs that already
   * know where they are going: a limbo reveal has a figure and a duration from
   * the start, so it books one glide rather than nudging the pitch every frame.
   * A crash round, which does not know, calls this as it goes and takes the
   * short default.
   */
  at: (multiplier: number, seconds?: number) => void;
  /** Let the note go. Safe to call twice; safe to call on unmount. */
  stop: (release?: number) => void;
}

/** The pitch, in hertz, for a multiplier. */
const climbPitch = (multiplier: number) =>
  Math.min(2200, Math.max(170, 180 * Math.pow(Math.max(multiplier, 1), 0.55)));

/**
 * Start the note a climbing round rides on.
 *
 * Returns `null` when there is no audio to be had, which is why every caller
 * treats the handle as optional rather than branching on mute itself. Once it
 * is running it keeps running through a mute - the bus takes care of that - so
 * a player who unmutes mid-round hears the rest of the climb.
 */
export const startClimb = (multiplier = 1): Climb | null => {
  if (!isAudible()) return null;

  const rig = getRig();
  if (!rig) return null;

  const held: Drone = drone(rig, {
    freq: climbPitch(multiplier),
    type: "triangle",
    attack: 0.12,
    gain: 0.1,
    cutoff: 3200,
  });

  return {
    at: (next, seconds = 0.1) => {
      held.glide(climbPitch(next), seconds);
      // Louder as it goes, but only a little: the point is tension, and a
      // round that is still climbing at 50x should not be painful.
      held.level(Math.min(0.2, 0.1 + Math.log10(Math.max(next, 1)) * 0.05), Math.max(seconds, 0.2));
    },
    stop: (release) => held.stop(release),
  };
};

/**
 * Hand the engine the player's preferences.
 *
 * Pushed in from `useSoundEngine` rather than read by the engine, so the rig
 * stays free of React and of the store - it is given a level, it does not go
 * looking for one.
 */
export const configureSound = (settings: { muted: boolean; volume: number }) =>
  applySettings(settings);
